import { Link, createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { EyeIcon } from "@heroicons/react/24/outline";

import {
  transactionsResourcePath,
  useGetTransactions,
} from "../../../services/queries/transactions";
import { useFilters } from "../../../hooks/useFilters";
import { Transaction } from "../../../services/queries/transactions/types";
import Pagination from "../../../components/Pagination";
import { formatDate } from "../../../utils";
import Table from "../../../components/Table";
import { useAppState } from "../../../contexts/AppContext";
import { requireRouteAccess } from "../../../utils/authorization";

type TransactionsTableColumns = Transaction & {
  ellipsis: "ellipsis";
  actions: "actions";
};

const columnHelper = createColumnHelper<TransactionsTableColumns>();

const columns = [
  columnHelper.accessor("orderNo", {
    cell: (info) => info.getValue(),
    header: "Order No",
  }),
  columnHelper.accessor((info) => `${info.baseCurrency} ${info.baseAmount}`, {
    cell: (info) => info.getValue(),
    header: "From",
  }),
  columnHelper.accessor((info) => `${info.quoteCurrency} ${info.quoteAmount}`, {
    cell: (info) => info.getValue(),
    header: "To",
  }),
  columnHelper.accessor("accountNumber", {
    cell: ({ row }) => {
      const { institution, accountName, accountNumber } = row.original;

      return (
        <div className="recipient-details">
          <p className="m-0">{institution}</p>
          <p className="m-0">{accountName}</p>
          <p className="m-0">{accountNumber}</p>
        </div>
      );
    },
    header: "Recipient",
  }),
  columnHelper.accessor("status", {
    cell: (info) => info.getValue(),
    header: "Status",
  }),
  columnHelper.accessor("created", {
    cell: (info) => formatDate(info.getValue()),
    header: "Created",
  }),
  columnHelper.accessor("actions", {
    cell: ({ row }) => {
      const { id } = row.original;

      return (
        <div className="flex gap-4">
          <Link
            to={`/transactions/${id}`}
            aria-label="View transaction"
            className="flex w-64 justify-center border p-3 text-gray-600 transition-colors hover:text-primary md:w-full"
          >
            <EyeIcon className="h-5 w-5" />
          </Link>
        </div>
      );
    },
    header: "",
  }),
];

export const Route = createFileRoute("/_app/transactions/")({
  beforeLoad: () => requireRouteAccess("/transactions"),
  component: Transactions,
});

function Transactions() {
  const {
    state: { business },
  } = useAppState();
  const businessId = business?.id || "";

  const {
    filters: { pageNumber, pageSize },
    setFilters,
    buildApiUrl,
  } = useFilters();
  const url = buildApiUrl(transactionsResourcePath);

  const handlePageChange = (selectedItem: { selected: number }) => {
    setFilters({ pageNumber: selectedItem.selected + 1 });
  };

  const { isLoading, data: transactionsData } = useGetTransactions(
    businessId,
    url,
  );

  const transactions = transactionsData?.data?.list || [];
  const totalRecords = transactionsData?.data?.meta?.total || 0;
  const pageCount = Math.ceil(totalRecords / Number(pageSize));

  return (
    <div className="transactions-page">
      <div className="bg-white overflow-x-auto relative p-6">
        <Table loading={isLoading} data={transactions} columns={columns} />

        <Pagination
          pageCount={pageCount}
          currentPage={pageNumber}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
